import { cellCenter } from '../grid.ts';
import { dist2, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { CliffPoint, CliffRules, CliffState } from '../galo-types.ts';
import { enemiesNear, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { useSkills } from './ghost.ts';
import { cliffOrder } from '../battle/cliff.ts';

/**
 * The lab's bot for the ranger's chapter 2 (docs/sendas/explorador.md, «El Acantilado del Flanco»; the common bot in
 * ai/senda-bot.ts hands these acts here). It only moves the hero, picks its target and taps the marks.
 *
 * - the cornice (`edge`): who knows stays by Sela, between her and the wall, the bow on what comes at her (the
 *   shriekers go to the last sound: her hammer or the bow, both there); who doesn't keeps off at the bow's reach.
 * - the wall and the night (`wall`, `night`): who knows reads the wall as a person does: which cracks are in the rope's
 *   reach, which have a ledge under them (firm or hollow) and which hang over the void (crumbling or hollow); it plans
 *   the fewest ropes to the goal with what it doesn't know yet taken for the best, shoots the first, and plans again
 *   with what it heard. Hanging, it shoots at once. At night it rings a hollow stone before a rope when shriekers are
 *   near, holds the bow while they swarm it, and steps out of the mother's ring. Who doesn't shoots the nearest crack
 *   it hasn't tried, down if it can, and fights as usual.
 */

const shoot = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  h.target = t.id;
  h.forced = false;
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/** How long the bot looks at the wall from a ledge it just came onto (where the cracks are, what is under them). */
const THINK = 48;
/** How long the bot looks from a crumbling crack it hangs from before it shoots on. */
const HANG_THINK = 24;
/** How long the bot takes to react to the mother's shriek coming. */
const REACT = 12;

const go = (s: BattleState, h: Hero, x: number, y: number): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 300)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > 400) orderMove(s, h, x, y);
};

/* ---------- the cornice ---------- */

const edgeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.cliff as CliffRules;
  const sela = s.senda?.npcs.find((n) => n.id === 'sela');
  if (!sela) return;
  // Who knows stays by her, between her and the wall (what comes at her comes past the bow); who doesn't keeps off at
  // the bow's reach, as a ranger does, and shoots from there.
  void r;
  const spot = knows ? { x: sela.x - 900, y: sela.y - 300 } : { x: sela.x + 4200, y: sela.y - 2600 };
  go(s, h, spot.x, spot.y);
  // The nearest to Sela first (what is on her), then whatever is in reach.
  const list = enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e));
  const t = nearestTo(list, sela.x, sela.y);
  if (t) shoot(s, h, t);
};

/* ---------- the wall ---------- */

type Node = number;

/** The points a node shoots from: a ledge's cells, a hanging crack's own point. */
const fromPts = (s: BattleState, r: CliffRules, node: Node): CliffPoint[] => {
  const n = r.ledges.length;
  if (node < n) return (r.ledges[node] as CliffRules['ledges'][number]).cells.map((id) => cellCenter(s.map.radius, id));
  const c = r.cracks[node - n] as CliffRules['cracks'][number];
  return [{ x: c.x, y: c.y }];
};

/**
 * The bot's ways from a node: a crack in reach, by what can be seen and heard. A ledge under it (`land`): firm unless
 * it sounded hollow. Over the void: crumbling unless it sounded hollow. Who doesn't know reads nothing of the sounds
 * but the last.
 */
const edgesOf = (s: BattleState, r: CliffRules, w: CliffState, node: Node): { crack: number; to: Node }[] => {
  const n = r.ledges.length;
  const pts = fromPts(s, r, node);
  const out: { crack: number; to: Node }[] = [];
  r.cracks.forEach((c, i) => {
    if (node === n + i) return;
    if (w.sounded[i] && c.kind === 1) return;
    if (!pts.some((p) => withinRange(p.x, p.y, c.x, c.y, r.rope - 200))) return;
    if (c.land >= 0 && c.land !== node) out.push({ crack: i, to: c.land });
    else if (c.land < 0) out.push({ crack: i, to: n + i });
    // Up the wall by a firm one over this ledge (or one not tried yet, that may be).
    if (c.land === node && c.top >= 0 && !(w.sounded[i] && c.kind !== 0)) out.push({ crack: i, to: c.top });
  });
  return out;
};

/** The first crack of the fewest ropes from `node` to the goal (−1: none known). */
const plan = (s: BattleState, r: CliffRules, w: CliffState, node: Node): number => {
  const goal = r.ledges.findIndex((l) => l.goal);
  const total = r.ledges.length + r.cracks.length;
  const first = new Array<number>(total).fill(-2);
  first[node] = -1;
  let frontier = [node];
  while (frontier.length) {
    const next: number[] = [];
    for (const v of frontier) {
      for (const e of edgesOf(s, r, w, v)) {
        if (first[e.to] !== -2) continue;
        first[e.to] = first[v] === -1 ? e.crack : (first[v] as number);
        if (e.to === goal) return first[e.to] as number;
        next.push(e.to);
      }
    }
    frontier = next;
  }
  return -1;
};

const wallBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.cliff as CliffRules;
  const w = s.senda?.cliff as CliffState | undefined;
  if (!w) return;
  const night = r.act === 'night';
  const swarm = w.shriekers.filter((id) => {
    const e = s.enemies.find((x) => x.id === id);
    return e && e.hp > 0 && withinRange(e.x, e.y, h.x, h.y, 3200);
  }).length;
  // The fight: shoot what is near, unless (who knows, at night) the shriekers are on us: the bow would keep them.
  const out = w.shriekers.length;
  const hold = knows && night && (swarm >= 2 || out >= 3);
  // (Walking to a spot to shoot the rope from: nothing else, or the bow stops it on the way.)
  const busy = w.job >= 0 && w.shotAt < 0 && w.hang < 0;
  const t = busy ? null : nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e) && !(hold && w.shriekers.includes(e.id))), h.x, h.y);
  if (t && !hold) shoot(s, h, t);
  else if (t && hold && w.mother.id === t.id) shoot(s, h, t);
  else h.target = 0;
  if (w.trip || w.job >= 0 || h.stun > 0) return;
  // The mother's ring coming: out of it first (on a ledge), and no rope till it is over.
  const M = r.night?.mother;
  if (knows && M && w.mother.ring >= 0) {
    // (A person takes a moment to see the ring coming: a second before it steps away.)
    if (w.hang < 0 && s.tick - w.mother.ring >= REACT && withinRange(h.x, h.y, w.mother.x, w.mother.y, M.r + 300) && w.on >= 0) {
      const pts = fromPts(s, r, w.on);
      let best: CliffPoint | null = null;
      let bd = -1;
      for (const p of pts) {
        const d = dist2(p.x, p.y, w.mother.x, w.mother.y);
        if (d > bd) {
          bd = d;
          best = p;
        }
      }
      if (best) go(s, h, best.x, best.y);
    }
    if (w.hang < 0) return;
  }
  const node: Node = w.hang >= 0 ? r.ledges.length + w.hang : w.on;
  if (node < 0) return;
  // Thinking before the next rope (what a person takes to look at the wall from a new ledge): only on a ledge.
  if (w.hang < 0 && (h.moving || s.tick - w.landed < THINK)) return;
  // Hanging, it takes a person a moment too to find the next crack in reach.
  if (w.hang >= 0 && w.gives - s.tick > (s.rules?.senda?.cliff?.hold ?? 96) - HANG_THINK) return;
  if (knows) {
    // At night: shriekers near, a stone on this ledge not ringing: ring it first, far from where we go.
    if (night && out >= 1 && w.hang < 0) {
      const si = r.stones.findIndex((st) => st.ledge === w.on);
      if (si >= 0 && !w.rung[si]) {
        cliffOrder(s, h, r.cracks.length + si);
        return;
      }
    }
    const c = plan(s, r, w, node);
    if (c >= 0) cliffOrder(s, h, c);
    return;
  }
  // The obvious: the nearest crack not tried yet, one going down if any; else back up a rope.
  const lv = node < r.ledges.length ? (r.ledges[node] as CliffRules['ledges'][number]).level : 9;
  const pts = fromPts(s, r, node);
  let best = -1;
  let bd = 0x7fffffff;
  r.cracks.forEach((c, i) => {
    if (i === w.hang || (w.sounded[i] && c.kind !== 2)) return;
    if (!pts.some((p) => withinRange(p.x, p.y, c.x, c.y, r.rope - 200))) return;
    const down = c.land >= 0 ? (r.ledges[c.land] as CliffRules['ledges'][number]).level > lv : true;
    const d = dist2(h.x, h.y, c.x, c.y) + (down ? 0 : 400_000_000);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  if (best >= 0) {
    cliffOrder(s, h, best);
    return;
  }
  const rope = w.ropes.find((x) => x.from === w.on || (r.cracks[x.crack] as CliffRules['cracks'][number]).land === w.on);
  if (rope) cliffOrder(s, h, rope.crack);
};

export const cliffBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.cliff;
  if (!r) return;
  if (r.act === 'edge') edgeBot(s, h, knows);
  else wallBot(s, h, knows);
};
